/**
 * deleteTicketType — RPC contract for the deleteTicketType callable, the only
 * path that destroys a ticket type. `ticket-type.entity.ts` marks `delete`
 * `.requiresHandler('deleteTicketType')`. The fact that decides this delete —
 * whether any ticket names the type, and whether its capacity ledger has
 * already been drawn against by a settled order whose tickets are not minted
 * yet — spans documents no Firestore rule can read, so the authorization is
 * the callable itself.
 *
 * The call names the type and the event that owns it, never the workspace:
 *
 * Fields justified:
 *   eventId — the type's parent event; the path is
 *             workspaces/{orgId}/events/{eventId}/ticketTypes/{typeId} and
 *             the type id alone does not locate the document.
 *   typeId  — the ticket type to delete.
 * Excluded:
 *   a confirmation flag — acknowledgement is what the form renders, and a
 *           flag the caller sets to true guards nothing the caller could not
 *           set anyway; the census inside the transaction is the guard.
 *
 * Output: a discriminated union on `outcome`, because a refusal is the
 * answer the operator asked for — the counts standing in the way, rendered
 * with the remedy — not an exception. An unauthenticated or unpermitted
 * caller still throws `HttpsError`.
 *
 * Output fields justified:
 *   quotaDeleted — whether the type's inventory document went with it. The
 *                  ledger is one document per type, so it is a fact rather
 *                  than a count, and it is the one leg of the cascade that
 *                  is not the type document itself.
 *   dependents   — the census, on refusal only.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { z } from "zod";
import { DependentsSchema } from "./dependents.const.ts";
import { Id } from "./workspace.entity.ts";

export const DeleteTicketTypeInput = z
	.object({
		orgId: Id,
		eventId: Id.describe("the event the ticket type being deleted belongs to"),
		typeId: Id.describe("the ticket type to delete"),
	})
	.strict();
export type DeleteTicketTypeInput = z.infer<typeof DeleteTicketTypeInput>;

export const DeleteTicketTypeOutput = z.discriminatedUnion("outcome", [
	z
		.object({
			outcome: z.literal("deleted"),
			quotaDeleted: z
				.boolean()
				.describe(
					"whether the type's capacity ledger was destroyed with it — false when the type never had one",
				),
		})
		.strict(),
	z
		.object({
			outcome: z.literal("refused"),
			dependents: DependentsSchema.describe(
				"what still names the ticket type, in counts — the refusal's whole substance",
			),
		})
		.strict(),
]);
export type DeleteTicketTypeOutput = z.infer<typeof DeleteTicketTypeOutput>;
